/*
 * The Cognito pool config, loaded before Amplify is configured (#671 A2.2d).
 *
 * `/api/auth/config` is selected by `COGNITO_POOL` on the server, so switching or reverting the
 * pool is an environment change and an app restart, not a rebuild.
 */
// `undefined` until the server has answered - deliberately NOT the baked config above.
//
// The baked config is the OLD pool, and the old pool has MFA switched off. Falling back to it
// therefore does not "keep the app working": it silently signs people in against a different pool
// with a weaker login policy, and the symptom is not an error but *the absence of an MFA prompt*,
// which reads like MFA is broken (#671). A request that fails here has to be visible.
let config

// The backend and vite start together, and nodemon restarts the backend on every route change, so
// the first request can land while it is briefly unavailable. Retry generously for that window: a
// restart takes about a second, and the cost of waiting is a moment's spinner, while the cost of
// guessing is the wrong pool.
const ATTEMPTS = 6
const RETRY_DELAY_MS = 1000

export const load = async () => {
  let lastError

  for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
    try {
      const response = await fetch('/api/auth/config')
      if (response.ok) {
        config = await response.json()
        // The pool id is not a secret (it is in the client config), but it is noise in the
        // production console and looks like an information leak; keep it out of the console.
        return config
      }
      lastError = new Error(`auth config returned ${response.status}`)
    } catch (err) {
      lastError = err
    }

    if (attempt < ATTEMPTS - 1) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS))
    }
  }

  // Null, not the baked config: the caller must decide what to do about it, and the only safe
  // answer is "do not configure Amplify against a pool nobody chose".
  console.error('Unable to load the auth config from the server', lastError)
  return null
}

export const get = () => {
  if (!config) {
    // Only reachable if something calls this before `load()` has succeeded. Saying so beats
    // "Cannot read properties of undefined", which is what a caller would otherwise report.
    throw new Error('The auth config has not loaded: no Cognito pool has been chosen yet')
  }
  return config
}

export default { load, get }
